<script setup lang="ts">
import type { Toast } from '@ts-toast/interfaces'

import { useToastItem } from '@hooks/toast/toast-item.hook'

import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import BtnIcon from '@UI/Buttons/BtnIcon.vue'
import IconContainer from '@UI/Other/IconContainer.vue'
import TextSecondaryMedium from '@UI/Typography/TextSecondaryMedium.vue'
import TextTertiary from '@UI/Typography/TextTertiary.vue'

defineOptions({
  name: 'ToastItem',
})

const props = defineProps<Props>()

const emit = defineEmits<{ dismiss: [id: string] }>()

interface Props {
  toast: Toast
}

const {
  icons,
  progress,
  swipeStyle,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  pauseProgress,
  resumeProgress,
} = useToastItem({ toast: props.toast })

const handlePointerUp = (): void => {
  onPointerUp({
    onEmitDismiss: () => {
      emit('dismiss', props.toast.id)
    },
  })
}

</script>

<template>
  <div
    :class="['toast-item', `toast-item--${toast.variant}`]"
    :style="swipeStyle"
    role="alert"
    aria-live="assertive"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerUp"
    @mouseenter="pauseProgress"
    @mouseleave="resumeProgress"
  >
    <div class="toast-item__content">
      <IconContainer
        :class="`toast-icon toast-icon--${toast.variant}`"
      >
        <FontAwesomeIcon
          :icon="icons[toast.variant]"
        />
      </IconContainer>
      <div class="toast-item__text">
        <TextSecondaryMedium
          is="span"
          v-if="toast.title"
        >
          {{ toast.title }}
        </TextSecondaryMedium>
        <TextTertiary is="span">
          {{ toast.description }}
        </TextTertiary>
      </div>
      <BtnIcon
        v-if="toast.closable"
        :icon="faXmark"
        color="transparent"
        aria-label="Close"
        size="small"
        @click:close="emit('dismiss', toast.id)"
        @pointerdown.stop
        @pointerup.stop
      />
    </div>
    <div
      v-if="toast.progress && toast.duration && toast.duration > 0"
      class="toast-item__progress"
    >
      <div
        class="toast-item__progress-bar"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="@scss/components/UI/Toast/ToastItem.scss" />
